import { Checkbox } from '@wheely/ui-kit';
import differenceInYears from 'date-fns/differenceInYears';
import React, { useCallback, useMemo, useState } from 'react';
import { Control, type UseControllerProps } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { usePhoneCodes } from '@/shared/hooks/usePhoneCodes';
import { CountryCode, TCountryCodes } from '@/shared/models/country-code';
import { capitalizeString } from '@/shared/utils/capitalize';
import { incorrectValueMessage, NAME_PATTERN, checkNoDigits } from '@/shared/utils/name-validation';
import { BottomButtons } from '@/shared/view/BottomButtons';
import { Section } from '@/shared/view/Section';
import { Button } from '@src-common/view/adapters/Button';
import { DateField } from '@src-common/view/adapters/reactHookForm/DateField';
import { PhoneField } from '@src-common/view/adapters/reactHookForm/PhoneField';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';
import { SelectFieldItem } from '@src-common/view/adapters/SelectField';

import { messages } from './messages';
import s from './styles.module.css';
import { ICreateDriverForm } from './types';

interface IDriverViewProps {
  control: Control<ICreateDriverForm>;
  isSubmitting?: boolean;
  countryCode: TCountryCodes;
  licensingAuthorityTypes: SelectFieldItem[];
  linkToLicensingAuthorities: string;
  linkToPrivacyPolicy: string;
}

type CreateDriverFormRules = UseControllerProps<ICreateDriverForm>['rules'];

const CreateDriverView: React.FC<IDriverViewProps> = ({
  isSubmitting,
  control,
  countryCode,
  licensingAuthorityTypes,
  linkToLicensingAuthorities,
  linkToPrivacyPolicy,
}) => {
  const intl = useIntl();
  const phoneCodes = usePhoneCodes();

  const [isContractActive, setIsContractActive] = useState(false);
  const handleChangeContractStatus = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    setIsContractActive(event?.currentTarget?.checked);
  }, []);

  const firstNameFieldRules = useMemo<CreateDriverFormRules>(
    () => ({
      pattern: {
        value: NAME_PATTERN,
        message: intl.formatMessage(incorrectValueMessage),
      },
    }),
    [intl],
  );

  const noDigitsFieldRules = useMemo<CreateDriverFormRules>(
    () => ({
      validate: value =>
        checkNoDigits(value as string | null, intl.formatMessage(incorrectValueMessage)),
    }),
    [intl],
  );

  return (
    <>
      <Section>
        <TextField
          name="firstName"
          title={intl.formatMessage(messages.firstNameTitle)}
          control={control}
          isRequired={true}
          rules={firstNameFieldRules}
          transformValue={capitalizeString}
        />
        <TextField
          name="lastName"
          title={intl.formatMessage(messages.lastNameTitle)}
          control={control}
          isRequired={true}
          rules={noDigitsFieldRules}
          transformValue={capitalizeString}
        />
        <PhoneField
          name="phoneNumber"
          namePhoneCode="phoneCode"
          title={intl.formatMessage(messages.phoneTitle)}
          control={control}
          isRequired={true}
          items={phoneCodes}
          description={
            countryCode === CountryCode.France &&
            intl.formatMessage(messages.phoneFranceDescription)
          }
        />
        <TextField
          name="email"
          title={intl.formatMessage(messages.emailTitle)}
          control={control}
          isRequired={true}
        />
        <DateField
          name="birthday"
          title={intl.formatMessage(messages.birthdayTitle)}
          control={control}
          isRequired={true}
          rules={{
            validate: value =>
              value && differenceInYears(new Date(), value as Date) < 18
                ? intl.formatMessage(messages.birthdayMinAge)
                : undefined,
          }}
        />
        {countryCode === CountryCode.GreatBritain && (
          <>
            <SelectField
              name="licensingAuthority"
              title={intl.formatMessage(messages.licensingAuthorityTitle)}
              description={intl.formatMessage(messages.licensingAuthorityDescription, {
                a: (text: React.ReactNode) => (
                  <a href={linkToLicensingAuthorities} target="_blank" rel="noreferrer">
                    {text}
                  </a>
                ),
              })}
              control={control}
              items={licensingAuthorityTypes}
            />
            <TextField
              name="licenseNumber"
              title={intl.formatMessage(messages.phdLicenseNumberTitle)}
              placeholder="12345678"
              description={intl.formatMessage(messages.phdLicenseNumberDescription)}
              control={control}
              isRequired={true}
            />
            <TextField
              name="ukNationalInsurance"
              title={intl.formatMessage(messages.nationalInsuranceTitle)}
              placeholder="AB123456C"
              isRequired={true}
              rules={{
                pattern: {
                  value: /^\w{2}\d{6}\w$/i,
                  message: intl.formatMessage(messages.nationalInsuranceError),
                },
              }}
              control={control}
            />
          </>
        )}
        {countryCode === CountryCode.France && (
          <TextField
            name="licenseNumber"
            title={intl.formatMessage(messages.vtcRegistrationNumberTitle)}
            placeholder="AAAA00000000"
            description={intl.formatMessage(messages.vtcRegistrationNumberDescription)}
            control={control}
            isRequired={true}
          />
        )}
        <TextField
          title={intl.formatMessage(messages.homeAddressTitle)}
          name="homeAddressLine1"
          isRequired={true}
          placeholder={intl.formatMessage(messages.addressLine1Placeholder)}
          control={control}
        />
        <TextField
          name="homeAddressLine2"
          placeholder={intl.formatMessage(messages.addressLine2Placeholder)}
          control={control}
        />
        <TextField
          name="homeAddressCity"
          isRequired={true}
          placeholder={intl.formatMessage(messages.cityPlaceholder)}
          control={control}
        />
        {countryCode === CountryCode.UnitedStates && (
          <TextField
            name="homeAddressState"
            isRequired={true}
            placeholder={intl.formatMessage(messages.statePlaceholder)}
            control={control}
          />
        )}
        {countryCode !== CountryCode.ArabEmirates && (
          <TextField
            name="homeAddressPostcode"
            isRequired={true}
            placeholder={intl.formatMessage(
              countryCode === CountryCode.UnitedStates
                ? messages.zipCodePlaceholder
                : messages.postcodePlaceholder,
            )}
            control={control}
          />
        )}

        {countryCode === CountryCode.Russia && (
          <label className={s.contractCheckbox}>
            <Checkbox onChange={handleChangeContractStatus} />
            <div>
              Я подтверждаю, что указанный водитель ознакомлен с{' '}
              <a href={linkToPrivacyPolicy} target="_blank" rel="noreferrer">
                Политикой Конфиденциальности
              </a>{' '}
              и согласен на обработку персональных данных, а также гарантирую наличие с ним
              Трудового договора и сразу же уведомлю Wheely при его расторжении
            </div>
          </label>
        )}
      </Section>
      <BottomButtons>
        <Button
          type="submit"
          isPending={isSubmitting}
          isDisabled={countryCode === 'RU' && !isContractActive}
        >
          {intl.formatMessage(messages.createButton)}
        </Button>
      </BottomButtons>
    </>
  );
};

export { CreateDriverView };
export type { IDriverViewProps };
